import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { format, isToday, differenceInCalendarDays } from 'date-fns';
import { Trash2 } from 'lucide-react-native';
import type { ChatRecord } from '~/powersync/generated/schema';
import { useCurrentChat } from '~/chat/hooks/useCurrentChat';
import { useDeleteProtection } from '~/global/hooks/useDeleteProtection';
import { RowMenu } from '@/components/ui/row-menu/RowMenu';
import { cn } from '@/lib/utils';
import { ChatRowPreview } from './ChatRowPreview';

/**
 * Shared chat-row container styling.
 *
 * Unselected rows use the panel's own `bg-surface` (rather than a transparent
 * base) so the iOS long-press context-menu preview — which snapshots just the
 * row — lifts a solid card instead of a see-through one. Against the surface
 * panel this looks identical to transparent in the list.
 */
const rowClassName = (isActive: boolean): string =>
  cn(
    'w-full gap-1 rounded-[10px] px-3 py-2.5',
    isActive ? 'bg-background' : 'bg-surface web:hover:bg-background/60',
  );

export interface ChatListRowProps {
  chat: ChatRecord;
  /** Time of the conversation's last activity (last message, else created). */
  timestamp: Date;
  /** Soft-delete this conversation (already advances the active chat). */
  onDelete: () => void;
  /** Called when this conversation is opened (e.g. to dismiss a mobile drawer). */
  onSelected?: () => void;
}

/** Today → time (2:14 PM); within a week → weekday (Mon); older → date (Mar 4). */
const formatRowTime = (date: Date): string => {
  if (isToday(date)) return format(date, 'p');
  if (differenceInCalendarDays(new Date(), date) < 7)
    return format(date, 'EEE');
  return format(date, 'MMM d');
};

/**
 * A single conversation in the chat-list sidebar. Tapping it switches the open
 * conversation; the active row gets a raised background. Title falls back to the
 * localized "Chat session" default until the AI worker names the chat.
 */
export const ChatListRow = ({
  chat,
  timestamp,
  onDelete,
  onSelected,
}: ChatListRowProps) => {
  const { t } = useTranslation('chat');
  const router = useRouter();
  const { chatId } = useCurrentChat();

  // Compare by uuid: the bare `/chat` landing carries no niceId in the URL.
  const isActive = chat.id === chatId;
  const title = chat.name?.trim() || t('list.defaultName');

  const { dispatchConfirmationDialog } = useDeleteProtection(onDelete, {
    title: t('list.deleteTitle'),
    description: t('list.deleteDescription'),
  });

  return (
    <RowMenu
      isActive={isActive}
      onSelect={() => {
        // Replace, not push: switching conversations shouldn't stack history
        // entries. A not-yet-synced chat has no niceId (usually the current
        // one): fall back to the landing, which holds on it rather than
        // dead-ending.
        router.replace(chat.niceId ? `/chat/${chat.niceId}` : '/chat');
        onSelected?.();
      }}
      className={cn(rowClassName(isActive), 'ios:justify-center')}
      actionsLabel={t('list.actions')}
      minHeight={62}
      actions={[
        {
          key: 'delete',
          label: t('list.delete'),
          icon: Trash2,
          systemImage: 'trash',
          variant: 'destructive',
          onPress: dispatchConfirmationDialog,
        },
      ]}
    >
      <View className="w-full flex-row items-center justify-between gap-2 web:pr-4">
        <Text
          numberOfLines={1}
          className="flex-1 text-sm font-inter-semibold text-text-primary"
        >
          {title}
        </Text>
        <Text className="text-[11px] font-inter-regular text-text-muted">
          {formatRowTime(timestamp)}
        </Text>
      </View>
      <ChatRowPreview chatId={chat.id} />
    </RowMenu>
  );
};
